// iter164.4 — Validation NPA multi-pays partagée (frontend).
// Miroir exact du backend core.validate_npa pour éviter la dérive.

/**
 * Valide un code postal selon le pays.
 * @param {string} npa - Code postal
 * @param {string} pays - Code pays ISO ('CH', 'FR', 'DE', ...) ou vide/null
 * @returns {{ok: boolean, message: string|null}}
 */
export function validateNpa(npa, pays) {
  const s = (npa || "").trim();
  const p = (pays || "").trim().toUpperCase();
  if (!s) return { ok: false, message: "Code postal requis" };
  if (p === "CH") {
    return /^[1-9]\d{3}$/.test(s)
      ? { ok: true, message: null }
      : { ok: false, message: "NPA suisse invalide (4 chiffres, 1000-9999)" };
  }
  if (p === "FR") {
    return /^\d{5}$/.test(s)
      ? { ok: true, message: null }
      : { ok: false, message: "Code postal français invalide (5 chiffres)" };
  }
  if (!p) {
    return /^\d{4,5}$/.test(s)
      ? { ok: true, message: null }
      : { ok: false, message: "Code postal invalide (4 chiffres CH ou 5 chiffres FR)" };
  }
  return /^[A-Za-z0-9][A-Za-z0-9 \-]{1,9}$/.test(s)
    ? { ok: true, message: null }
    : { ok: false, message: "Code postal invalide (2 à 10 caractères)" };
}

/** Placeholder à afficher dans un champ NPA selon le pays sélectionné. */
export function npaPlaceholder(pays) {
  const p = (pays || "").trim().toUpperCase();
  if (p === "FR") return "75001";
  if (p === "CH") return "1217";
  if (!p) return "1217 (CH) ou 75001 (FR)";
  return "12345";
}

/**
 * iter164.5 — Cherche les localités correspondant à un code postal.
 *
 * Sources publiques gratuites (aucune clé) :
 *   CH/DE/AT → openplzapi.org
 *   FR       → geo.api.gouv.fr (Étalab)
 *   Autres   → non supporté, retourne []
 *
 * @param {string} npa
 * @param {string} pays
 * @returns {Promise<Array<{name: string, canton?: string}>>}
 */
export async function lookupLocality(npa, pays) {
  const s = (npa || "").trim();
  const p = (pays || "").trim().toUpperCase() || "CH";
  if (!s) return [];
  try {
    if (p === "CH" || p === "DE" || p === "AT") {
      // Validation NPA min avant appel réseau (évite 400+ requests inutiles)
      if (p === "CH" && !/^[1-9]\d{3}$/.test(s)) return [];
      if ((p === "DE" || p === "AT") && !/^\d{4,5}$/.test(s)) return [];
      const url = `https://openplzapi.org/${p.toLowerCase()}/Localities?postalCode=${encodeURIComponent(s)}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return [];
      const data = await res.json();
      return (Array.isArray(data) ? data : []).map((it) => ({
        name: it.name || "",
        canton: it.canton?.name || it.federalState?.name || undefined,
      })).filter((it) => it.name);
    }
    if (p === "FR") {
      if (!/^\d{5}$/.test(s)) return [];
      const url = `https://geo.api.gouv.fr/communes?codePostal=${encodeURIComponent(s)}&fields=nom,codeDepartement&limit=15`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) return [];
      const data = await res.json();
      return (Array.isArray(data) ? data : []).map((it) => ({
        name: it.nom || "",
        canton: it.codeDepartement || undefined,
      })).filter((it) => it.name);
    }
  } catch {
    return [];
  }
  return [];
}
